import DateUtils from "../../../../../../../../core/services/date-utils";
import {PreventionInvalidityCode} from "../preventative-interactions";
import {BaseImportTransformer, toImportCodeUpper, toPartNumberUpper} from "./shared";

/**
 * @param {Object} item
 */
function parseVendorPriceAndDates(item) {
    const vendorPrice =
        item.vendorPrice !== undefined && item.vendorPrice !== null && item.vendorPrice !== ""
            ? parseFloat(item.vendorPrice.toString().replaceAll("$", "").replaceAll(",", "").trim())
            : undefined;

    const startDateTime = item.startDateTime?.trim() ? DateUtils.localToServer(item.startDateTime.trim()) : undefined;
    const endDateTime = item.endDateTime?.trim() ? DateUtils.localToServer(item.endDateTime.trim()) : undefined;

    return {
        vendorPrice: vendorPrice !== undefined && !isNaN(vendorPrice) ? vendorPrice : undefined,
        startDateTime:
            startDateTime && DateUtils.instance.isDayjs(startDateTime) && startDateTime.isValid()
                ? startDateTime.format(DateUtils.withoutOffsetFormat)
                : undefined,
        endDateTime:
            endDateTime && DateUtils.instance.isDayjs(endDateTime) && endDateTime.isValid()
                ? endDateTime.format(DateUtils.withoutOffsetFormat)
                : undefined,
    };
}

/**
 * Product vendors import — part + vendor resolution, optional price and date window.
 * Follows the multi-phase contract on {@link BaseImportTransformer}: batched part and vendor
 * lookups with no batch-level bail-out; codes accumulate per row (e.g. invalid part and vendor).
 */
export class ProductVendorsImportTransformer extends BaseImportTransformer {
    /**
     * @param {Array<Object|null|undefined>} mappedData
     * @param {boolean} isFinalRun
     */
    async run(mappedData, isFinalRun = false) {
        const rows = mappedData ?? [];
        const preventativeIssues = [];

        /** @type {{index: number, item: *, partNoUpper: string, vendorCode: string, codes: string[]}[]} */
        const rowState = rows.map((item, index) => ({
            index,
            item,
            partNoUpper: toPartNumberUpper(item?.partNumber),
            vendorCode: toImportCodeUpper(item?.vendorCode),
            codes: [],
        }));

        // ── Phase 1: preliminary (CSV-only) ─────────────────────────────────────
        for (const rs of rowState) {
            if (!rs.item) continue;
            if (!rs.partNoUpper) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
            if (!rs.vendorCode) {
                rs.codes.push(PreventionInvalidityCode.INVALID_VENDOR);
            }
        }

        // ── Phase 2: batched part lookup (all non-empty part numbers on any row) ───
        const uniquePartNumbersUpper = [...new Set(rowState.map((r) => r.partNoUpper).filter(Boolean))].sort();
        /** @type {Record<string, *>} */
        let partIds = {};
        if (uniquePartNumbersUpper.length > 0) {
            const partBundle = await this.ctx.fetchPartInfoByPartNumbers(uniquePartNumbersUpper);
            partIds = partBundle.partIds;
        }

        // ── Phase 3: batched vendor lookup (all non-empty vendor codes on any row) ─
        const uniqueVendorCodes = [...new Set(rowState.map((r) => r.vendorCode).filter(Boolean))].sort();
        /** @type {Record<string, *>} */
        let vendorIds = {};
        if (uniqueVendorCodes.length > 0) {
            const vendorBundle = await this.ctx.fetchVendorInfoByVendorCodes(uniqueVendorCodes);
            vendorIds = vendorBundle.vendorIds;
        }

        // ── Phase 4: unknown part ─────────────────────────────────────────────────
        for (const rs of rowState) {
            if (!rs.item) continue;
            if (rs.partNoUpper && !partIds[rs.partNoUpper]) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
        }

        // ── Phase 5: unknown vendor ───────────────────────────────────────────────
        for (const rs of rowState) {
            if (!rs.item) continue;
            const vendorId = rs.vendorCode ? vendorIds[rs.vendorCode] ?? null : null;
            if (rs.vendorCode && !vendorId) {
                rs.codes.push(PreventionInvalidityCode.INVALID_VENDOR);
            }
        }

        // ── Emit merged issues ───────────────────────────────────────────────────
        for (const rs of rowState) {
            if (!rs.item) {
                preventativeIssues.push({
                    rowIndex: rs.index,
                    originalRow: rows[rs.index],
                    invalidityCodes: [PreventionInvalidityCode.INVALID_PART],
                });
                continue;
            }
            if (!rs.codes.length) continue;
            preventativeIssues.push({
                rowIndex: rs.index,
                originalRow: rs.item,
                invalidityCodes: [...new Set(rs.codes)],
            });
        }

        const issueIndexSet = new Set(preventativeIssues.map((p) => p.rowIndex));

        const apiRequests = rows.map((item, index) => {
            if (!item) {
                return isFinalRun
                    ? null
                    : {partId: null, partNumber: undefined, vendorCode: undefined, vendorId: null};
            }

            const rs = rowState[index];
            const partId = rs.partNoUpper ? partIds[rs.partNoUpper] : undefined;
            const vendorId = rs.vendorCode ? vendorIds[rs.vendorCode] ?? null : null;
            const hasIssue = issueIndexSet.has(index);

            if (hasIssue) {
                return isFinalRun
                    ? null
                    : {
                        partId: partId ?? null,
                        partNumber: item.partNumber,
                        vendorCode: item.vendorCode,
                        vendorId: vendorId ?? null,
                    };
            }

            const {vendorPrice, startDateTime, endDateTime} = parseVendorPriceAndDates(item);

            return {
                id: item.id ? Number(item.id) : undefined,
                partId,
                partNumber: rs.partNoUpper,
                vendorCode: rs.vendorCode,
                vendorId,
                vendorPartNumber: toImportCodeUpper(item?.vendorPartNumber) || undefined,
                vendorPrice,
                startDateTime,
                endDateTime,
            };
        });

        return {apiRequests, preventativeIssues};
    }
}
